// src/common/ui/page-renderer.js
// Page rendering and highlighting utilities

import { escapeHTML } from './dom-utils.js';
import { renderPdfPages } from './pdf-page-viewer.js';
import { pageJumpLabel, pageTypeBadgeHtml } from './page-type.js';

const SUPERS = "\u2070\u00b9\u00b2\u00b3\u2074\u2075\u2076\u2077\u2078\u2079";
const SUPMAP = { "0":"\u2070","1":"\u00b9","2":"\u00b2","3":"\u00b3","4":"\u2074","5":"\u2075","6":"\u2076","7":"\u2077","8":"\u2078","9":"\u2079" };
const SUPERSCRIPT_TO_DIGIT = {
  "\u2070": "0", "\u00b9": "1", "\u00b2": "2", "\u00b3": "3", "\u2074": "4",
  "\u2075": "5", "\u2076": "6", "\u2077": "7", "\u2078": "8", "\u2079": "9"
};
const SUPERSCRIPT_FOOTNOTE_RE = /[\u2070\u00b9\u00b2\u00b3\u2074-\u2079]+\d*/gu;
const SUPERSCRIPT_DEF_LINE_RE = /^([\u2070\u00b9\u00b2\u00b3\u2074-\u2079]+\d*)\s+(.+)$/u;
const PAREN_NUMBER_WORDS = new Set([
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
  'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
  'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty',
  'un', 'une', 'deux', 'trois', 'quatre', 'cinq', 'sept', 'huit', 'neuf', 'dix',
]);
const DOC_VIEW_MODE_KEY = 'fc_doc_view_mode';
const PDF_ZOOM_KEY = 'fc_pdf_zoom';
let currentPdfBytes = null;
let currentDocPage = 1;
let pageTrackerBound = false;
// Pages array of the last render — each loaded document has its own array, so
// reference identity detects document switches.
let lastRenderedPages = null;
// Latest re-render callback for the view toggle / zoom controls. The DOM
// listeners are bound once, so they dereference this instead of closing over
// the first document's render arguments.
let viewToggleHandler = null;

function getPdfZoom() {
  try {
    const value = Number(localStorage.getItem(PDF_ZOOM_KEY));
    return Number.isFinite(value) ? Math.min(2.0, Math.max(0.65, value)) : 1.15;
  } catch {
    return 1.15;
  }
}

function setPdfZoom(value) {
  const zoom = Math.min(2.0, Math.max(0.65, value));
  try {
    localStorage.setItem(PDF_ZOOM_KEY, String(zoom));
  } catch {
    // Ignore storage failures; the in-memory render still works.
  }
  return zoom;
}

function getDocViewMode() {
  try {
    return localStorage.getItem(DOC_VIEW_MODE_KEY) || 'markdown';
  } catch {
    return 'markdown';
  }
}

function setDocViewMode(mode) {
  try {
    localStorage.setItem(DOC_VIEW_MODE_KEY, mode);
  } catch {
    // Ignore storage failures; the in-memory render still works.
  }
}

function pageNumberFromElement(el) {
  const match = el?.id?.match?.(/^page-(\d+)$/);
  return match ? Number(match[1]) : null;
}

function getCenteredPageNumber(wrap) {
  const pageEls = Array.from(wrap?.querySelectorAll?.('.doc-page') || []);
  if (!pageEls.length) return currentDocPage;

  const wrapRect = wrap.getBoundingClientRect();
  const centerY = wrapRect.top + (wrapRect.height / 2);
  let best = null;

  for (const pageEl of pageEls) {
    const rect = pageEl.getBoundingClientRect();
    const page = pageNumberFromElement(pageEl);
    if (!page) continue;
    const distance = Math.abs((rect.top + rect.height / 2) - centerY);
    if (!best || distance < best.distance) {
      best = { page, distance };
    }
  }

  return best?.page || currentDocPage;
}

function setCurrentDocPage(page, jump) {
  const numeric = Number(page);
  if (!Number.isFinite(numeric) || numeric < 1) return;
  currentDocPage = numeric;
  if (jump && jump.value !== String(numeric)) {
    jump.value = String(numeric);
  }
}

function ensurePageTracker(wrap, jump) {
  if (pageTrackerBound || !wrap || !jump) return;
  pageTrackerBound = true;

  let ticking = false;
  wrap.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      setCurrentDocPage(getCenteredPageNumber(wrap), jump);
    });
  });

  jump.addEventListener('change', () => {
    const page = Number(jump.value);
    const target = document.getElementById(`page-${page}`);
    setCurrentDocPage(page, jump);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function ensureViewToggle(onChange) {
  // Always adopt the latest callback: the click listeners below are bound
  // once, and calling a stale closure would re-render a previous document.
  viewToggleHandler = onChange;

  const tools = document.querySelector('#doc-pane .pane-tools');
  if (!tools) return;

  let toggle = tools.querySelector('.doc-view-toggle');
  if (!toggle) {
    toggle = document.createElement('div');
    toggle.className = 'doc-view-toggle';
    toggle.setAttribute('role', 'group');
    toggle.setAttribute('aria-label', 'Document view');
    toggle.innerHTML = `
      <button type="button" class="doc-view-toggle-btn" data-view-mode="markdown">Text</button>
      <button type="button" class="doc-view-toggle-btn" data-view-mode="pdf">PDF</button>
    `;
    tools.insertBefore(toggle, tools.firstChild);

    toggle.addEventListener('click', (event) => {
      const button = event.target?.closest?.('[data-view-mode]');
      if (!button) return;
      const mode = button.dataset.viewMode || 'markdown';
      setDocViewMode(mode);
      viewToggleHandler?.(mode);
    });
  }

  let zoom = tools.querySelector('.pdf-zoom-controls');
  if (!zoom) {
    zoom = document.createElement('div');
    zoom.className = 'pdf-zoom-controls';
    zoom.setAttribute('role', 'group');
    zoom.setAttribute('aria-label', 'PDF zoom');
    zoom.innerHTML = `
      <button type="button" class="pdf-zoom-btn" data-pdf-zoom="out" title="Zoom out">-</button>
      <button type="button" class="pdf-zoom-readout" data-pdf-zoom="reset" title="Reset zoom"></button>
      <button type="button" class="pdf-zoom-btn" data-pdf-zoom="in" title="Zoom in">+</button>
    `;
    tools.insertBefore(zoom, toggle.nextSibling);

    zoom.addEventListener('click', (event) => {
      const button = event.target?.closest?.('[data-pdf-zoom]');
      if (!button) return;
      const action = button.dataset.pdfZoom;
      const current = getPdfZoom();
      if (action === 'out') setPdfZoom(current - 0.15);
      if (action === 'in') setPdfZoom(current + 0.15);
      if (action === 'reset') setPdfZoom(1.15);
      viewToggleHandler?.(getDocViewMode());
    });
  }

  const active = getDocViewMode();
  for (const button of toggle.querySelectorAll('[data-view-mode]')) {
    button.classList.toggle('active', button.dataset.viewMode === active);
    button.setAttribute('aria-pressed', String(button.dataset.viewMode === active));
  }
  zoom.classList.toggle('active', active === 'pdf');
  const readout = zoom.querySelector('.pdf-zoom-readout');
  if (readout) readout.textContent = `${Math.round(getPdfZoom() * 100)}%`;
}

function isProbableParenthesizedFootnoteRef(text, offset, rawNum) {
  const charBefore = offset > 0 ? text[offset - 1] : '';
  if (charBefore && /[\p{L}\p{N}]/u.test(charBefore)) return false;

  const previousText = text.slice(0, offset).trimEnd();
  const previousToken = previousText.match(/([A-Za-zÀ-ÖØ-öø-ÿ0-9]+)$/)?.[1]?.toLowerCase();
  if (previousToken && (PAREN_NUMBER_WORDS.has(previousToken) || previousToken === rawNum)) {
    return false;
  }

  const nextNonSpace = text.slice(offset + rawNum.length + 2).match(/\S/)?.[0];
  if (nextNonSpace && /[\p{L}\p{N}]/u.test(nextNonSpace)) return false;

  return true;
}

/**
 * Build a loose context regex for matching
 * @param {string} s - Context string
 * @returns {RegExp|null}
 */
function buildLooseContextRegex(s) {
  const cleaned = (s || "").replace(/\s+/g, " ").replace(/\$\{\s*\}\^\d+/g, "").trim();
  const tokens = cleaned.split(/[^A-Za-zÀ-ÖØ-öø-ÿ0-9]+/).filter(w => w.length >= 3);
  if (!tokens.length) return null;
  const windowTokens = tokens.slice(0, 60);
  const esc = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const gap = String.raw`[\s\S]{0,120}`;
  try { return new RegExp(windowTokens.map(esc).join(gap), 'i'); } catch { return null; }
}

/**
 * Highlight context in HTML page content
 * @param {string} htmlPage - HTML page content
 * @param {string} contextStr - Context string
 * @param {string} fullContextStr - Full context string
 * @returns {string} - Page with highlighted context
 */
export function highlightContext(htmlPage, contextStr, fullContextStr) {
  if (!htmlPage) return htmlPage;
  const tryStrs = [contextStr, fullContextStr].filter(Boolean);
  for (const s of (tryStrs || [])) {
    const exact = escapeHTML(s);
    if (exact && htmlPage.includes(exact)) {
      // Simple replacement - avoid replacing inside HTML tags by checking context
      return htmlPage.replace(new RegExp(exact.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), (match, offset, string) => {
        // Check if we're inside an HTML tag
        const before = string.substring(Math.max(0, offset - 50), offset);
        const after = string.substring(offset, Math.min(string.length, offset + match.length + 50));
        // If there's an unclosed < before or a > after, we're likely in a tag
        const lastOpenTag = before.lastIndexOf('<');
        const lastCloseTag = before.lastIndexOf('>');
        if (lastOpenTag > lastCloseTag) {
          // We're inside a tag, don't replace
          return match;
        }
        return `<mark class="fn-ctx">${match}</mark>`;
      });
    }
    const loose = buildLooseContextRegex(s);
    if (loose) {
      const m = htmlPage.match(loose);
      if (m && m[0]) {
        // Simple replacement with tag check
        return htmlPage.replace(new RegExp(m[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), (match, offset, string) => {
          const before = string.substring(Math.max(0, offset - 50), offset);
          const lastOpenTag = before.lastIndexOf('<');
          const lastCloseTag = before.lastIndexOf('>');
          if (lastOpenTag > lastCloseTag) {
            return match;
          }
          return `<mark class="fn-ctx">${match}</mark>`;
        });
      }
    }
  }
  return htmlPage;
}

/**
 * Clean whitespace (consolidate multiple spaces/tabs into single space)
 * @param {string} s - String to clean
 * @returns {string} - Cleaned string
 */
function cleanWhitespace(s) {
  return s.replace(/[ \t]+/g, ' ').trim();
}

function normalizeSuperscriptNumber(value) {
  return String(value || '').split('').map(ch => SUPERSCRIPT_TO_DIGIT[ch] || ch).join('');
}

/**
 * Split combined footnote text that may bundle multiple footnotes
 * Similar to Python _split_combined_footnote_text
 * Handles patterns like:
 *   [^0]:
 *       ${ }^{2}$ First footnote text...
 *       ${ }^{3}$ Second footnote text...
 *       4. Third footnote text...
 *       (5) Parenthesized footnote text...
 * @param {string} text - Combined footnote text
 * @returns {Map<string, string>} - Map of footnote number to content
 */
function splitCombinedFootnoteText(text) {
  const s = text.trim();
  if (!s) {
    return new Map();
  }

  const result = new Map();

  // Canonical Markdown definitions are already normalized by the backend,
  // e.g. ``[^53]: source text``. Handle these before the legacy OCR formats;
  // otherwise the renderer detects the definition block but extracts no
  // footnotes and silently drops it from the text view.
  const markdownItem = /^\[\^([^\]]+)\]:\s*(.*)$/gm;
  const markdownMatches = [];
  let markdownMatch;
  while ((markdownMatch = markdownItem.exec(s)) !== null) {
    markdownMatches.push({
      key: markdownMatch[1],
      index: markdownMatch.index,
      contentStart: markdownMatch.index + markdownMatch[0].length - markdownMatch[2].length,
      end: markdownMatch.index + markdownMatch[0].length,
    });
  }
  if (markdownMatches.length > 0) {
    for (let i = 0; i < markdownMatches.length; i++) {
      const current = markdownMatches[i];
      const nextStart = i + 1 < markdownMatches.length
        ? markdownMatches[i + 1].index
        : s.length;
      const content = cleanWhitespace(s.substring(current.contentStart, nextStart));
      if (content && current.key !== '0') {
        result.set(current.key, content);
      }
    }
    return result;
  }
  
  // Strategy: Find all LaTeX markers first, then process numbered list items
  // LaTeX markers: ${ }^{n}$ or ${}^{n}$
  const latexItem = /\$\s*(?:\{\s*\})?\s*\^\{\s*(\d+)\s*\}\$/g;
  const latexMatches = [];
  let match;
  
  while ((match = latexItem.exec(s)) !== null) {
    latexMatches.push({
      number: match[1],
      start: match.index,
      end: match.index + match[0].length
    });
  }
  
  // Process LaTeX markers - extract content after each marker
  if (latexMatches.length > 0) {
    for (let i = 0; i < latexMatches.length; i++) {
      const m = latexMatches[i];
      const start = m.end;
      const end = (i + 1 < latexMatches.length) ? latexMatches[i + 1].start : s.length;
      const chunk = cleanWhitespace(s.substring(start, end));
      if (chunk) {
        result.set(m.number, chunk);
      }
    }
  }
  
  // Now process numbered list items, parenthesized definitions, and unicode
  // superscript definitions at start of line.
  const lines = s.split(/\n/);
  let currentNum = null;
  let currentContent = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Check if this line starts a new numbered footnote
    // Pattern: optional whitespace, number, optional period, space, then content
    const numberedMatch = trimmed.match(/^(\d+)\.?\s+(.+)$/);
    const parenthesizedMatch = trimmed.match(/^\((\d{1,4})\)\s*(.*)$/);
    const superscriptMatch = trimmed.match(SUPERSCRIPT_DEF_LINE_RE);
    
    if (numberedMatch || parenthesizedMatch || superscriptMatch) {
      // Save previous footnote if any
      if (currentNum !== null && currentContent.length > 0) {
        const content = cleanWhitespace(currentContent.join(' '));
        // Only add if not already set by LaTeX marker
        if (!result.has(currentNum) && content) {
          result.set(currentNum, content);
        }
      }
      
      // Start new footnote
      if (parenthesizedMatch) {
        currentNum = parenthesizedMatch[1];
        currentContent = [parenthesizedMatch[2]];
      } else if (superscriptMatch) {
        currentNum = normalizeSuperscriptNumber(superscriptMatch[1]);
        currentContent = [superscriptMatch[2]];
      } else {
        currentNum = numberedMatch[1];
        currentContent = [numberedMatch[2]];
      }
    } else if (currentNum !== null) {
      // Continuation line - check if it's indented (part of current footnote)
      // or if it contains a LaTeX marker (which would end this footnote)
      if (trimmed === '') {
        // Empty line - continue collecting (might be part of multi-line footnote)
        continue;
      }
      
      // Check if this line contains a LaTeX marker (ends current footnote)
      if (latexItem.test(trimmed)) {
        // Save current footnote
        if (currentContent.length > 0) {
          const content = cleanWhitespace(currentContent.join(' '));
          if (!result.has(currentNum) && content) {
            result.set(currentNum, content);
          }
        }
        currentNum = null;
        currentContent = [];
        // Reset regex lastIndex
        latexItem.lastIndex = 0;
      } else if (/^\s/.test(line)) {
        // Indented line - continuation of current footnote
        currentContent.push(trimmed);
      } else {
        // Non-indented, non-empty line that doesn't start with a number
        // This might be a continuation or the end - be conservative and end here
        if (currentContent.length > 0) {
          const content = cleanWhitespace(currentContent.join(' '));
          if (!result.has(currentNum) && content) {
            result.set(currentNum, content);
          }
        }
        currentNum = null;
        currentContent = [];
      }
    }
  }
  
  // Save last footnote if any
  if (currentNum !== null && currentContent.length > 0) {
    const content = cleanWhitespace(currentContent.join(' '));
    if (!result.has(currentNum) && content) {
      result.set(currentNum, content);
    }
  }
  
  return result;
}

function isLikelyNumberedFootnoteBlockStart(lines, index, expectedFootnoteNumbers) {
  if (!expectedFootnoteNumbers || expectedFootnoteNumbers.size === 0) {
    return false;
  }

  const nonEmptyLines = lines
    .map((line, lineIndex) => ({ line: line.trim(), lineIndex }))
    .filter(({ line }) => line);
  const currentNonEmptyIndex = nonEmptyLines.findIndex(({ lineIndex }) => lineIndex === index);
  const isLowerPageBlock = currentNonEmptyIndex >= Math.floor(nonEmptyLines.length * 0.45);
  if (!isLowerPageBlock) {
    return false;
  }

  let foundExpectedCount = 0;
  for (let i = index; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      continue;
    }
    if (trimmed.startsWith('#')) {
      break;
    }

    const numberedMatch = trimmed.match(/^(\d+)\.?\s+(.+)$/);
    const superscriptMatch = trimmed.match(SUPERSCRIPT_DEF_LINE_RE);
    const candidateNumber = numberedMatch
      ? numberedMatch[1]
      : (superscriptMatch ? normalizeSuperscriptNumber(superscriptMatch[1]) : null);
    if (candidateNumber && expectedFootnoteNumbers.has(candidateNumber)) {
      foundExpectedCount++;
      if (foundExpectedCount >= 2 || expectedFootnoteNumbers.size === 1) {
        return true;
      }
      continue;
    }

    if (foundExpectedCount === 0) {
      return false;
    }
  }

  return false;
}

function isLikelySuperscriptFootnoteBlockStart(lines, index, expectedFootnoteNumbers) {
  if (!expectedFootnoteNumbers || expectedFootnoteNumbers.size === 0) {
    return false;
  }

  let foundExpectedCount = 0;
  for (let i = index; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      continue;
    }
    if (trimmed.startsWith('#')) {
      break;
    }

    const superscriptMatch = trimmed.match(SUPERSCRIPT_DEF_LINE_RE);
    if (!superscriptMatch) {
      break;
    }

    const candidateNumber = normalizeSuperscriptNumber(superscriptMatch[1]);
    if (!expectedFootnoteNumbers.has(candidateNumber)) {
      break;
    }

    foundExpectedCount++;
    if (foundExpectedCount >= 2 || expectedFootnoteNumbers.size === 1) {
      return true;
    }
  }

  return isLikelyNumberedFootnoteBlockStart(lines, index, expectedFootnoteNumbers);
}

function hasExpectedSuperscriptDefinitionBlock(lines, expectedFootnoteNumbers) {
  if (!expectedFootnoteNumbers || expectedFootnoteNumbers.size === 0) {
    return false;
  }

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const superscriptMatch = trimmed.match(SUPERSCRIPT_DEF_LINE_RE);
    if (
      superscriptMatch
      && expectedFootnoteNumbers.has(normalizeSuperscriptNumber(superscriptMatch[1]))
      && isLikelySuperscriptFootnoteBlockStart(lines, i, expectedFootnoteNumbers)
    ) {
      return true;
    }
  }

  return false;
}

function parseDefinitionStartNumber(trimmed) {
  const markdownMatch = trimmed.match(/^\[\^([^\]]+)\]:\s*(.*)$/);
  if (markdownMatch) {
    return markdownMatch[1];
  }
  const latexMatch = trimmed.match(/^\$\s*(?:\{\s*\})?\s*\^\{\s*(\d+)\s*\}\$\s*(.*)$/);
  if (latexMatch) {
    return latexMatch[1];
  }
  const superscriptMatch = trimmed.match(SUPERSCRIPT_DEF_LINE_RE);
  if (superscriptMatch) {
    return normalizeSuperscriptNumber(superscriptMatch[1]);
  }
  const parenthesizedMatch = trimmed.match(/^\((\d{1,4})\)\s*(.*)$/);
  if (parenthesizedMatch) {
    return parenthesizedMatch[1];
  }
  const numberedMatch = trimmed.match(/^(\d+)\.?\s+(.+)$/);
  if (numberedMatch) {
    return numberedMatch[1];
  }
  return null;
}

function isDefinitionStartLine(trimmed) {
  return parseDefinitionStartNumber(trimmed) !== null;
}

function findFootnoteDefinitionBlock(lines, expectedFootnoteNumbers) {
  const nonEmptyLines = lines
    .map((line, lineIndex) => ({ line: line.trim(), lineIndex }))
    .filter(({ line }) => line);
  if (!nonEmptyLines.length) {
    return null;
  }

  const lowerPageIndex = Math.floor(nonEmptyLines.length * 0.45);
  let best = null;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const firstNum = parseDefinitionStartNumber(trimmed);
    if (!firstNum) {
      continue;
    }

    const nonEmptyIndex = nonEmptyLines.findIndex(({ lineIndex }) => lineIndex === i);
    const isLowerPage = nonEmptyIndex >= lowerPageIndex;
    const isExplicitDefinition =
      /^\[\^/.test(trimmed)
      || /^\$\s*(?:\{\s*\})?\s*\^\{/.test(trimmed)
      || SUPERSCRIPT_DEF_LINE_RE.test(trimmed)
      || /^\(\d{1,4}\)\s+/.test(trimmed);

    let definitionCount = 0;
    let matchedExpectedCount = 0;
    let end = i;

    for (let j = i; j < lines.length; j++) {
      const nextTrimmed = lines[j].trim();
      if (!nextTrimmed) {
        end = j + 1;
        continue;
      }
      if (nextTrimmed.startsWith('#')) {
        break;
      }

      const nextNum = parseDefinitionStartNumber(nextTrimmed);
      if (nextNum) {
        definitionCount++;
        if (expectedFootnoteNumbers.has(nextNum)) {
          matchedExpectedCount++;
        }
        end = j + 1;
        continue;
      }

      // A non-definition line after definitions is only a continuation if it is
      // indented. Otherwise we stop, preserving later body text by default.
      if (/^(?:\s{4,}|\t)/.test(lines[j]) && definitionCount > 0) {
        end = j + 1;
        continue;
      }
      break;
    }

    const credible = isExplicitDefinition
      ? (
        definitionCount >= 2
        || (definitionCount === 1 && expectedFootnoteNumbers.has(firstNum))
      )
      : (
        matchedExpectedCount > 0
        && expectedFootnoteNumbers.has(firstNum)
        && (
          definitionCount >= 2
          || matchedExpectedCount === 1
        )
      );
    if (!credible || (!isLowerPage && !isExplicitDefinition)) {
      continue;
    }

    const candidate = { start: i, end, definitionCount, matchedExpectedCount };
    if (
      !best
      || candidate.matchedExpectedCount > best.matchedExpectedCount
      || (
        candidate.matchedExpectedCount === best.matchedExpectedCount
        && candidate.definitionCount > best.definitionCount
      )
    ) {
      best = candidate;
    }
  }

  return best;
}

/**
 * Convert markdown to HTML with proper formatting
 * Handles headings, paragraphs, footnote references, and extracts footnotes
 * @param {string} markdown - Markdown content
 * @returns {string} - HTML content with footnotes at the bottom
 */
function markdownToHtml(markdown) {
  if (!markdown) return '';
  
  // First, find all footnote references to know which numbers we're looking for
  // Pattern matches: [^key], ${ }^{n}$, ${ }^{n} »$ (with optional chars before final $), or unicode superscripts
  const fnRefPattern = /\[\^([^\]]+)\]|\$\s*(?:\{\s*\})?\s*\^\{\s*(\d+)\s*\}\s*[^$]*\$|([\u2070\u00b9\u00b2\u00b3\u2074-\u2079]+\d*)/gu;
  const expectedFootnoteNumbers = new Set();
  
  let match;
  while ((match = fnRefPattern.exec(markdown)) !== null) {
    let fnId = null;
    if (match[1]) {
      // Markdown style [^key]
      fnId = match[1];
    } else if (match[2]) {
      // LaTeX style ${ }^{n}$
      fnId = match[2];
    } else if (match[3]) {
      // Unicode superscript
      fnId = normalizeSuperscriptNumber(match[3]);
    }
    if (fnId && fnId !== '0') {
      expectedFootnoteNumbers.add(fnId);
    }
  }

  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();
    if (
      !trimmed
      || /^\[\^/.test(trimmed)
      || /^\$\s*(?:\{\s*\})?\s*\^\{/.test(trimmed)
      || SUPERSCRIPT_DEF_LINE_RE.test(trimmed)
      || /^\(\d{1,4}\)\s+/.test(trimmed)
    ) {
      continue;
    }
    const plainRefPattern = /(^|[^\d])(\d{1,4})(?=[.,;:)](?:\s|$))/g;
    let plainMatch;
    while ((plainMatch = plainRefPattern.exec(line)) !== null) {
      const markerStart = plainMatch.index + plainMatch[1].length;
      const previousText = line.slice(0, markerStart);
      if (/^\s*$/.test(previousText)) {
        continue;
      }
      expectedFootnoteNumbers.add(plainMatch[2]);
    }
  }
  
  // Extract footnote definitions and process content line by line
  const footnoteDefs = new Map();
  const lines = markdown.split('\n');
  const definitionBlock = findFootnoteDefinitionBlock(lines, expectedFootnoteNumbers);
  if (definitionBlock) {
    const combinedText = lines.slice(definitionBlock.start, definitionBlock.end).join('\n');
    const splitFootnotes = splitCombinedFootnoteText(combinedText);
    splitFootnotes.forEach((fnContent, fnNum) => {
      if (fnNum !== '0') {
        footnoteDefs.set(fnNum, fnContent);
      }
    });
  }
  const hasSuperscriptDefinitionBlock = hasExpectedSuperscriptDefinitionBlock(lines, expectedFootnoteNumbers);
  const contentLines = [];
  let i = 0;
  
  // Process lines to extract footnotes and build content
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (definitionBlock && i >= definitionBlock.start && i < definitionBlock.end) {
      i = definitionBlock.end;
      continue;
    }
    
    // Check if this is a footnote definition with [^key]: marker
    const fnMatch = trimmed.match(/^\[\^([^\]]+)\]:\s*(.*)$/);
    if (!definitionBlock && fnMatch) {
      const key = fnMatch[1];
      let content = fnMatch[2];
      
      // Collect continuation lines (indented lines after the definition)
      i++;
      while (i < lines.length) {
        const nextLine = lines[i];
        // Check if this could be a new footnote definition
        const nextTrimmed = nextLine.trim();
        if (nextTrimmed.match(/^\[\^([^\]]+)\]:/)) {
          // This is a new footnote definition - stop collecting
          break;
        }
        if (/^(?:\s{4,}|\t)/.test(nextLine)) {
          // Indented line - continuation of footnote
          content += '\n' + nextLine;
          i++;
        } else if (nextTrimmed === '') {
          // Empty line - stop collecting (footnotes don't span empty lines)
          break;
        } else {
          // Non-indented, non-empty line - end of footnote
          break;
        }
      }
      
      // Split combined footnote text (may contain multiple footnotes)
      const splitFootnotes = splitCombinedFootnoteText(content.trim());
      if (splitFootnotes.size > 0) {
        // Multiple footnotes found - add each one (filter out "0")
        splitFootnotes.forEach((fnContent, fnNum) => {
          if (fnNum !== '0') {
            footnoteDefs.set(fnNum, fnContent);
          }
        });
      } else {
        // Single footnote - use the original key (filter out "0")
        const trimmedContent = content.trim();
        if (trimmedContent && key !== '0') {
          footnoteDefs.set(key, trimmedContent);
        }
      }
      
      // Skip the footnote definition lines (already processed)
      continue;
    }
    
    // Parenthesized definitions establish the valid "(n)" references for this page.
    // Only line-start definitions are considered; inline "(n)" prose remains body text.
    const parenthesizedDefMatch = trimmed.match(/^\((\d{1,4})\)\s*(.*)$/);
    if (!definitionBlock && parenthesizedDefMatch && footnoteDefs.size === 0) {
      const footnoteLines = [];

      while (i < lines.length) {
        const nextLine = lines[i];
        const nextTrimmed = nextLine.trim();
        if (nextTrimmed.startsWith('#')) {
          break;
        }
        footnoteLines.push(nextLine);
        i++;
      }

      const combinedText = footnoteLines.join('\n');
      const splitFootnotes = splitCombinedFootnoteText(combinedText);
      splitFootnotes.forEach((fnContent, fnNum) => {
        if (fnNum !== '0') {
          footnoteDefs.set(fnNum, fnContent);
        }
      });

      continue;
    }

    const superscriptDefMatch = trimmed.match(SUPERSCRIPT_DEF_LINE_RE);
    if (
      !definitionBlock
      &&
      superscriptDefMatch
      && footnoteDefs.size === 0
      && expectedFootnoteNumbers.has(normalizeSuperscriptNumber(superscriptDefMatch[1]))
      && isLikelySuperscriptFootnoteBlockStart(lines, i, expectedFootnoteNumbers)
    ) {
      const footnoteLines = [];

      while (i < lines.length) {
        const nextLine = lines[i];
        const nextTrimmed = nextLine.trim();
        if (nextTrimmed.startsWith('#')) {
          break;
        }
        footnoteLines.push(nextLine);
        i++;
      }

      const combinedText = footnoteLines.join('\n');
      const splitFootnotes = splitCombinedFootnoteText(combinedText);
      splitFootnotes.forEach((fnContent, fnNum) => {
        if (fnNum !== '0') {
          footnoteDefs.set(fnNum, fnContent);
        }
      });

      continue;
    }

    // If no [^key]: markers found, look for numbered list footnotes matching expected numbers
    if (
      !definitionBlock
      &&
      expectedFootnoteNumbers.size > 0
      && footnoteDefs.size === 0
      && !hasSuperscriptDefinitionBlock
      && isLikelyNumberedFootnoteBlockStart(lines, i, expectedFootnoteNumbers)
    ) {
      const numberedMatch = trimmed.match(/^(\d+)\.?\s+(.+)$/);
      if (numberedMatch) {
        const fnNum = numberedMatch[1];
        if (expectedFootnoteNumbers.has(fnNum)) {
          let content = numberedMatch[2];
          const footnoteLines = [line];
          
          // Collect continuation lines
          i++;
          let foundNumbers = new Set([fnNum]);
          let inFootnoteBlock = true;
          
          while (i < lines.length && inFootnoteBlock) {
            const nextLine = lines[i];
            const nextTrimmed = nextLine.trim();
            const nextNumberedMatch = nextTrimmed.match(/^(\d+)\.?\s+(.+)$/);
            
            if (nextNumberedMatch) {
              const nextFnNum = nextNumberedMatch[1];
              if (expectedFootnoteNumbers.has(nextFnNum)) {
                // Another expected footnote
                foundNumbers.add(nextFnNum);
                footnoteLines.push(nextLine);
                i++;
              } else if (foundNumbers.size === expectedFootnoteNumbers.size) {
                // Found all expected footnotes
                break;
              } else {
                // Unexpected number, but might still be collecting
                i++;
                continue;
              }
            } else if (!nextTrimmed || nextTrimmed.startsWith('#')) {
              // Empty line or heading - end of footnotes
              if (nextTrimmed.startsWith('#')) break;
              footnoteLines.push(nextLine);
              i++;
            } else {
              // Continuation line (indented or not)
              footnoteLines.push(nextLine);
              i++;
            }
          }
          
          // Split the collected footnote lines
          const combinedText = footnoteLines.join('\n');
          const splitFootnotes = splitCombinedFootnoteText(combinedText);
          splitFootnotes.forEach((fnContent, fnNum) => {
            // Filter out "0" footnotes
            if (fnNum !== '0') {
              footnoteDefs.set(fnNum, fnContent);
            }
          });
          
          // Skip the processed lines
          continue;
        }
      }
    }
    
    // Not a footnote definition - add to content
    contentLines.push(line);
    i++;
  }
  
  // Process content lines
  const htmlParts = [];
  let currentParagraph = [];
  
  for (let i = 0; i < contentLines.length; i++) {
    const line = contentLines[i];
    const trimmed = line.trim();
    
    // Empty line - end current paragraph if any
    if (trimmed === '') {
      if (currentParagraph.length > 0) {
        const paraText = currentParagraph.join('\n').trim();
        if (paraText) {
          htmlParts.push(`<p>${processInlineMarkdown(paraText, { footnoteNumbers: footnoteDefs })}</p>`);
        }
        currentParagraph = [];
      }
      continue;
    }
    
    // Check for heading (1-6 #)
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      // End current paragraph if any
      if (currentParagraph.length > 0) {
        const paraText = currentParagraph.join('\n').trim();
        if (paraText) {
          htmlParts.push(`<p>${processInlineMarkdown(paraText, { footnoteNumbers: footnoteDefs })}</p>`);
        }
        currentParagraph = [];
      }
      
      const level = headingMatch[1].length;
      const headingText = headingMatch[2];
      htmlParts.push(`<h${level}>${processInlineMarkdown(headingText, { footnoteNumbers: footnoteDefs })}</h${level}>`);
      continue;
    }
    
    // Regular line - add to current paragraph
    currentParagraph.push(line);
  }
  
  // Handle remaining paragraph
  if (currentParagraph.length > 0) {
    const paraText = currentParagraph.join('\n').trim();
    if (paraText) {
      htmlParts.push(`<p>${processInlineMarkdown(paraText, { footnoteNumbers: footnoteDefs })}</p>`);
    }
  }
  
  // Add footnotes section if there are any
  if (footnoteDefs.size > 0) {
    htmlParts.push('<hr class="footnote-separator">');
    htmlParts.push('<div class="footnotes">');
    
    // Sort footnotes by number (convert to number for proper sorting)
    const sortedKeys = Array.from(footnoteDefs.keys()).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      // If not numeric, sort alphabetically
      return a.localeCompare(b);
    });
    
    sortedKeys.forEach(key => {
      // Filter out "0" footnotes
      if (key === '0') {
        return;
      }
      const content = footnoteDefs.get(key);
      if (content) {
        const processedContent = processInlineMarkdown(content);
        htmlParts.push(`<div class="footnote" id="fn-${escapeHTML(key)}">`);
        htmlParts.push(`<span class="badge badge-footnote">Fn. ${escapeHTML(key)}</span> `);
        htmlParts.push(`<span class="footnote-content">${processedContent}</span>`);
        htmlParts.push('</div>');
      }
    });
    htmlParts.push('</div>');
  }
  
  return htmlParts.join('\n');
}

/**
 * Process inline markdown (footnote references, basic formatting)
 * @param {string} text - Text with inline markdown
 * @returns {string} - HTML with processed inline elements
 */
export function processInlineMarkdown(text, options = {}) {
  if (!text) return '';
  const optionFootnoteNumbers = options.footnoteNumbers || options.parenthesizedFootnoteNumbers;
  const footnoteNumbers = optionFootnoteNumbers instanceof Map
    ? new Set(optionFootnoteNumbers.keys())
    : new Set(optionFootnoteNumbers || []);
  
  // Clean up markdown artifacts like [MATH]
  text = text.replace(/\[MATH\]/g, '').trim();
  
  // Use placeholders to avoid conflicts with HTML escaping
  const placeholders = new Map();
  let placeholderIndex = 0;
  
  // Process LaTeX-style footnote references matching Python pattern: 
  // \$\s*(?:\{\s*\})?\s*\^\{\s*(\d+)\s*\}\s*[^$]*\$
  // This matches: ${ }^{n}$, ${}^{n}$, ${ }^{n} »$ (with optional characters before final $)
  // Replace with placeholder first (before HTML escaping)
  // Filter out "0" footnote references
  let processed = text.replace(/\$\s*(?:\{\s*\})?\s*\^\{\s*(\d+)\s*\}\s*[^$]*\$/g, (match, num) => {
    if (num === '0') {
      // Remove "0" footnote references
      return '';
    }
    const placeholder = `__FNREF_${placeholderIndex}__`;
    placeholders.set(placeholder, `<span class="badge badge-footnote">Fn. ${escapeHTML(num)}</span>`);
    placeholderIndex++;
    return placeholder;
  });

  // Process parenthesized footnote references only when markdownToHtml has already
  // collected matching "(n)" definitions from the page footnote zone.
  processed = processed.replace(/\((\d{1,4})\)/g, (match, num, offset, fullText) => {
    if (
      num === '0'
      || !footnoteNumbers.has(num)
      || !isProbableParenthesizedFootnoteRef(fullText, offset, num)
    ) {
      return match;
    }
    const placeholder = `__FNREF_${placeholderIndex}__`;
    placeholders.set(placeholder, `<span class="badge badge-footnote">Fn. ${escapeHTML(num)}</span>`);
    placeholderIndex++;
    return placeholder;
  });

  // Process plain OCR footnote references, e.g. "planned for May 2021 50.".
  // Only numbers that have a collected footnote definition are converted.
  processed = processed.replace(/(^|[^\d])(\d{1,4})(?=([.,;:)]|$))/g, (match, before, num, _punct, offset, fullText) => {
    if (num === '0' || !footnoteNumbers.has(num)) {
      return match;
    }
    const markerStart = offset + before.length;
    const previousText = fullText.slice(0, markerStart);
    if (/^\s*$/.test(previousText) || /\n\s*$/.test(previousText)) {
      return match;
    }
    const placeholder = `__FNREF_${placeholderIndex}__`;
    placeholders.set(placeholder, `<span class="badge badge-footnote">Fn. ${escapeHTML(num)}</span>`);
    placeholderIndex++;
    return before + placeholder;
  });
  
  // Process LaTeX-style superscripts in content: $base^{superscript}$
  // Examples: $8^{e}$, $2^{\mathrm{e}}$, ${ }^{\circ} 4$
  // Pattern: $...^{...}$ where we extract the superscript part
  processed = processed.replace(/\$([^$]*?)\^\{([^}]+)\}([^$]*?)\$/g, (match, before, superscript, after) => {
    // Extract content from LaTeX commands like \mathrm{e} -> e, \circ -> °
    let supContent = superscript
      .replace(/\\mathrm\{([^}]+)\}/g, '$1')  // \mathrm{e} -> e
      .replace(/\\circ/g, '°')                 // \circ -> °
      .replace(/\\[a-zA-Z]+/g, '')            // Remove other LaTeX commands
      .trim();
    
    // Build the result: before + superscript + after
    let result = before.trim();
    if (supContent) {
      result += `<sup>${escapeHTML(supContent)}</sup>`;
    }
    result += after.trim();
    return result;
  });

  // Clean up any remaining generic LaTeX-ish OCR fragments, e.g.
  // $\mathrm{CM} / \mathrm{ECF}$ -> CM / ECF
  processed = processed.replace(/\$([^$]+)\$/g, (match, inner) => {
    if (!/\\[a-zA-Z]+|[\^_]\{/.test(inner)) {
      return match;
    }
    const plain = inner
      .replace(/\\[a-zA-Z]+\{([^{}]*)\}/g, '$1')
      .replace(/\\circ/g, '°')
      .replace(/([\w°])\s*[\^_]\{\s*([^{}]+?)\s*\}/g, '$1$2')
      .replace(/([\w°])\s*[\^_]\s*([A-Za-z0-9°]+)/g, '$1$2')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/[{}]/g, '')
      .trim();
    return plain || match;
  });
  
  // Escape HTML (this won't affect our placeholders since they use underscores and numbers)
  let html = escapeHTML(processed);
  
  // Restore placeholders (now they're safe HTML)
  // Use a more specific replacement to avoid conflicts
  placeholders.forEach((replacement, placeholder) => {
    // Escape the placeholder for regex, but it should be safe as-is
    const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    html = html.replace(new RegExp(escapedPlaceholder, 'g'), replacement);
  });

  // Render the emphasis syntax emitted by OCR providers.  This must happen
  // after HTML escaping (so the captured text is already safe) and after
  // footnote placeholders have been restored.  Strong emphasis is handled
  // first so constructs such as **_erga omnes_** can be nested correctly.
  html = html.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_\n]+?)__/g, '<strong>$1</strong>');
  html = html.replace(/(^|[^\w*])\*([^*\n]+?)\*(?!\w)/g, '$1<em>$2</em>');
  html = html.replace(/(^|[^\w_])_([^_\n]+?)_(?!\w)/g, '$1<em>$2</em>');
  
  // Convert footnote references [^key] to badge
  // Filter out "0" footnote references
  html = html.replace(/\[\^([^\]]+)\]/g, (match, key) => {
    if (key === '0') {
      // Remove "0" footnote references
      return '';
    }
    return `<span class="badge badge-footnote">Fn. ${escapeHTML(key)}</span>`;
  });
  
  // Convert unicode superscript footnote runs to badges. Match the whole run so
  // multi-digit markers like "³⁶" become "Fn. 36" instead of "Fn. 6".
  html = html.replace(SUPERSCRIPT_FOOTNOTE_RE, (match) => {
    const normalized = normalizeSuperscriptNumber(match);
    if (normalized === '0') {
      // Remove "0" footnote references
      return '';
    }
    return `<span class="badge badge-footnote">Fn. ${normalized}</span>`;
  });
  
  // Preserve simple line breaks inside paragraphs/fields
  html = html.replace(/\r?\n/g, '<br>');
  
  return html;
}

/**
 * Highlight footnote references and definitions in markdown
 * @param {string} pageMd - Page markdown content
 * @param {string} fnId - Footnote ID
 * @returns {string} - Markdown with highlighted footnotes
 */
export function highlightFootnoteInMarkdown(pageMd, fnId) {
  const m = /^p(\d+)-(.+)$/.exec(fnId);
  if (!m) return markdownToHtml(pageMd);
  const local = m[2];

  // Convert to HTML first
  let html = markdownToHtml(pageMd);

  // Highlight definitions
  html = html.replace(new RegExp(String.raw`<span class="badge badge-footnote">Fn\. ${local}</span>`, 'g'), 
    `<mark class="fn-def"><span class="badge badge-footnote">Fn. ${local}</span></mark>`);
  html = html.replace(new RegExp(String.raw`<div class="footnote" id="fn-${local}">`, 'g'),
    `<div class="footnote fn-def-highlight" id="fn-${local}">`);

  // Highlight references
  html = html.replace(new RegExp(String.raw`<span class="badge badge-footnote">Fn\. ${local}</span>`, 'g'),
    `<mark class="fn-ref"><span class="badge badge-footnote">Fn. ${local}</span></mark>`);

  return html;
}

/**
 * Render pages with optional focus highlighting
 * @param {Array} pages - Array of page objects
 * @param {Object} focus - Focus object with fnId, page, context, fullContext
 */
export function renderPages(pages, focus, options = {}) {
  const wrap = document.getElementById("doc-pages");
  const jump = document.getElementById("page-jump");
  if (!wrap || !jump) return;
  ensurePageTracker(wrap, jump);

  // Page position and PDF bytes are per-document state: on a document switch
  // they must not leak from the previous document (stale bytes would render
  // the old PDF; the old scroll position would anchor the new document at the
  // previous document's page).
  const isNewDocument = pages !== lastRenderedPages;
  lastRenderedPages = pages;
  if (isNewDocument) {
    currentDocPage = Number(focus?.page) || 1;
    currentPdfBytes = options.pdfBytes || null;
  } else if (options.pdfBytes) {
    currentPdfBytes = options.pdfBytes;
  }

  const visiblePage = isNewDocument
    ? currentDocPage
    : (getCenteredPageNumber(wrap) || Number(jump.value) || currentDocPage);
  const anchorPage = options.anchorPage || visiblePage;

  ensureViewToggle(() => {
    // Recompute the visible page at toggle time (renderPages reads the live
    // DOM) instead of pinning the anchor from this render.
    const toggleOptions = { ...options, pdfBytes: currentPdfBytes };
    delete toggleOptions.anchorPage;
    renderPages(pages, focus, toggleOptions);
  });

  if (getDocViewMode() === 'pdf') {
    renderPdfPages({
      pages,
      pdfBytes: options.pdfBytes || currentPdfBytes,
      focus,
      anchorPage,
      onPageCentered: (page) => setCurrentDocPage(page, jump),
      scale: getPdfZoom(),
      wrap,
      jump,
    }).catch((err) => {
      console.error('[PDF_VIEW] Failed to render PDF view:', err);
      wrap.innerHTML = '<div class="pdf-unavailable">PDF view could not be rendered. Switch back to Text view to continue.</div>';
    });
    return;
  }

  wrap.innerHTML = "";
  jump.innerHTML = "";

  const total = pages.length;
  pages.forEach((p, idx) => {
    const pageNum = p.page_number ?? (idx + 1);
    const art = document.createElement("article");
    art.className = "doc-page";
    art.id = `page-${pageNum}`;

    const md = p.markdown || "";
    let bodyHtml;

    if (focus?.fnId && String(focus.page) === String(pageNum)) {
      bodyHtml = highlightFootnoteInMarkdown(md, focus.fnId);
      bodyHtml = highlightContext(bodyHtml, focus.context, focus.fullContext);
    } else if (focus?.page && String(focus.page) === String(pageNum)) {
      bodyHtml = markdownToHtml(md);
      bodyHtml = highlightContext(bodyHtml, focus.context, focus.fullContext);
    } else {
      bodyHtml = markdownToHtml(md);
    }

    art.innerHTML = `
      <div class="page-head">Page ${pageNum} / ${total}${pageTypeBadgeHtml(p)}</div>
      <div class="page-body">${bodyHtml}</div>
    `;
    wrap.appendChild(art);

    const opt = document.createElement("option");
    opt.value = String(pageNum);
    opt.textContent = pageJumpLabel(pageNum, p);
    jump.appendChild(opt);
  });

  if (anchorPage && !focus?.fnId) {
    const target = document.getElementById(`page-${anchorPage}`);
    target?.scrollIntoView({ behavior: "auto", block: "start" });
    if (target && target.scrollWidth > target.clientWidth) {
      target.scrollLeft = Math.max(0, (target.scrollWidth - target.clientWidth) / 2);
    }
    setCurrentDocPage(anchorPage, jump);
  }
}

/**
 * Selection context menu for parsing selected text as citation
 * @private
 */
let selectionContextMenu = null;

/**
 * AbortController for the current text selection handler, used to clean up
 * event listeners when setupTextSelectionHandler is called again.
 * @private
 */
let selectionHandlerAbort = null;

/**
 * Create or get the selection context menu element
 * @returns {HTMLElement}
 */
function getSelectionContextMenu() {
  if (selectionContextMenu) return selectionContextMenu;
  
  selectionContextMenu = document.createElement('div');
  selectionContextMenu.className = 'selection-context-menu';
  selectionContextMenu.innerHTML = `
    <button class="selection-menu-btn parse-citation-btn">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 5v14M5 12h14"/>
      </svg>
      <span>Parse as Citation</span>
    </button>
  `;
  selectionContextMenu.style.display = 'none';
  document.body.appendChild(selectionContextMenu);
  
  return selectionContextMenu;
}

/**
 * Hide the selection context menu
 */
function hideSelectionMenu() {
  const menu = getSelectionContextMenu();
  menu.style.display = 'none';
}

/**
 * Show the selection context menu near the selection
 * @param {number} x - X position
 * @param {number} y - Y position
 */
function showSelectionMenu(x, y) {
  const menu = getSelectionContextMenu();
  menu.style.display = 'block';
  
  // Position menu near the selection, but ensure it stays in viewport
  const menuRect = menu.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  let left = x;
  let top = y + 10; // Offset below the selection
  
  // Adjust if menu would go off-screen
  if (left + menuRect.width > viewportWidth) {
    left = viewportWidth - menuRect.width - 10;
  }
  if (top + menuRect.height > viewportHeight) {
    top = y - menuRect.height - 10; // Show above instead
  }
  
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

/**
 * Get the page number from a selection
 * @param {Selection} selection - The window selection
 * @returns {number|null} - Page number or null if not in a page
 */
function getPageNumberFromSelection(selection) {
  if (!selection || selection.isCollapsed) return null;
  
  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;
  
  // Find the closest .doc-page ancestor
  let pageEl = container.nodeType === Node.TEXT_NODE 
    ? container.parentElement?.closest('.doc-page')
    : container.closest?.('.doc-page');
  
  if (!pageEl) return null;
  
  // Extract page number from id (format: "page-N")
  const match = pageEl.id?.match(/^page-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Set up text selection handling for parsing citations
 * @param {Object} options - Configuration options
 * @param {Function} options.onParseSelection - Callback when user wants to parse selection as citation
 * @param {Function} options.getPageText - Function to get page text by page number
 */
export function setupTextSelectionHandler(options = {}) {
  const { onParseSelection, getPageText } = options;

  if (!onParseSelection) {
    console.warn('[SelectionHandler] onParseSelection callback required');
    return;
  }

  const wrap = document.getElementById("doc-pages");
  if (!wrap) {
    console.warn('[SelectionHandler] doc-pages container not found');
    return;
  }

  // Tear down previous handler listeners to avoid stacking
  if (selectionHandlerAbort) {
    selectionHandlerAbort.abort();
  }
  selectionHandlerAbort = new AbortController();
  const { signal } = selectionHandlerAbort;

  // Track if we're handling a selection
  let isHandlingSelection = false;

  // Handle mouseup to detect selections
  wrap.addEventListener('mouseup', (e) => {
    // Small delay to let the selection finalize
    setTimeout(() => {
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();

      if (!selectedText || selectedText.length < 5) {
        hideSelectionMenu();
        return;
      }

      const pageNum = getPageNumberFromSelection(selection);
      if (!pageNum) {
        hideSelectionMenu();
        return;
      }

      console.log(`[SelectionHandler] Selected "${selectedText.substring(0, 50)}..." on page ${pageNum}`);

      // Show the context menu
      showSelectionMenu(e.clientX, e.clientY);

      // Store selection data for the button handler
      const menu = getSelectionContextMenu();
      menu.dataset.selectedText = selectedText;
      menu.dataset.pageNumber = String(pageNum);
    }, 10);
  }, { signal });

  // Handle clicking outside to hide menu
  document.addEventListener('mousedown', (e) => {
    const menu = getSelectionContextMenu();
    if (!menu.contains(e.target)) {
      hideSelectionMenu();
    }
  }, { signal });

  // Handle parse citation button click
  const menu = getSelectionContextMenu();
  const parseBtn = menu.querySelector('.parse-citation-btn');

  parseBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isHandlingSelection) return;
    isHandlingSelection = true;
    
    const selectedText = menu.dataset.selectedText;
    const pageNumber = parseInt(menu.dataset.pageNumber, 10);
    
    if (!selectedText || !pageNumber) {
      isHandlingSelection = false;
      hideSelectionMenu();
      return;
    }
    
    // Show loading state
    parseBtn.disabled = true;
    parseBtn.innerHTML = `
      <span class="spinner"></span>
      <span>Parsing...</span>
    `;
    
    try {
      // Get page texts for context
      let pageText = '';
      let prevPageText = null;
      let nextPageText = null;
      
      if (getPageText) {
        pageText = getPageText(pageNumber) || '';
        prevPageText = pageNumber > 1 ? getPageText(pageNumber - 1) : null;
        nextPageText = getPageText(pageNumber + 1);
      } else {
        // Fallback: get from DOM
        const pageEl = document.getElementById(`page-${pageNumber}`);
        pageText = pageEl?.querySelector('.page-body')?.textContent || '';
        
        const prevPageEl = document.getElementById(`page-${pageNumber - 1}`);
        prevPageText = prevPageEl?.querySelector('.page-body')?.textContent || null;
        
        const nextPageEl = document.getElementById(`page-${pageNumber + 1}`);
        nextPageText = nextPageEl?.querySelector('.page-body')?.textContent || null;
      }
      
      // Call the callback
      await onParseSelection({
        selectedText,
        pageNumber,
        pageText,
        prevPageText,
        nextPageText,
      });
      
      console.log('[SelectionHandler] Citation parse request completed');
    } catch (err) {
      console.error('[SelectionHandler] Error parsing selection:', err);
    } finally {
      // Reset button state
      parseBtn.disabled = false;
      parseBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>Parse as Citation</span>
      `;
      isHandlingSelection = false;
      hideSelectionMenu();
      
      // Clear the selection
      window.getSelection()?.removeAllRanges();
    }
  }, { signal });

  console.log('[SelectionHandler] Text selection handler initialized');
}
